import { useRef, useEffect } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import './backgroundFlow.scss';

/**
 * Page-wide background of vertical waveform lines that flow as you scroll.
 * Acts as a continuous "signal thread" extending from the hero violin
 * through the rest of the page.
 */
export default function BackgroundFlow() {
  const canvasRef = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrame;
    let time = 0;
    let scrollY = window.scrollY;
    const isMobile = window.matchMedia('(max-width: 640px)').matches;
    const dpr = Math.min(window.devicePixelRatio || 1, isMobile ? 1.25 : 1.75);

    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const handleScroll = () => {
      scrollY = window.scrollY;
    };

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Flow "threads" — each with its own color, position, frequency, amplitude, and flow speed.
    const lines = isMobile
      ? [
          { color: [34, 211, 238], baseX: 0.2, amp: 36, freq: 0.009, phase: 0, speed: 0.45 },
          { color: [217, 70, 239], baseX: 0.55, amp: 56, freq: 0.0065, phase: 1.5, speed: 0.32 },
          { color: [251, 191, 36], baseX: 0.82, amp: 30, freq: 0.011, phase: 3.0, speed: 0.55 },
        ]
      : [
          { color: [34, 211, 238], baseX: 0.14, amp: 70, freq: 0.0078, phase: 0, speed: 0.5 },
          { color: [167, 243, 252], baseX: 0.32, amp: 45, freq: 0.011, phase: 0.8, speed: 0.42 },
          { color: [217, 70, 239], baseX: 0.52, amp: 110, freq: 0.0055, phase: 1.6, speed: 0.28 },
          { color: [240, 171, 252], baseX: 0.72, amp: 60, freq: 0.0095, phase: 2.4, speed: 0.55 },
          { color: [251, 191, 36], baseX: 0.9, amp: 50, freq: 0.012, phase: 3.4, speed: 0.6 },
        ];

    // Glowing particles drifting down along the lines
    const particleCount = isMobile ? 8 : 14;
    const particles = Array.from({ length: particleCount }).map((_, i) => ({
      line: i % lines.length,
      y: Math.random() * window.innerHeight,
      vy: 0.4 + Math.random() * 0.8,
      size: 1 + Math.random() * 1.6,
    }));

    // Subtle music notes drifting along the same threads
    const noteGlyphs = ['♪', '♫', '♬', '♩'];
    const noteCount = isMobile ? 4 : 7;
    const notes = Array.from({ length: noteCount }).map((_, i) => ({
      line: i % lines.length,
      y: Math.random() * window.innerHeight,
      vy: 0.25 + Math.random() * 0.5,
      glyph: noteGlyphs[Math.floor(Math.random() * noteGlyphs.length)],
      size: 12 + Math.random() * 6,
      rotation: (Math.random() - 0.5) * 0.4,
    }));

    const draw = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Fade-paint instead of clear, to leave a soft trail behind moving elements
      ctx.fillStyle = 'rgba(10, 10, 26, 0.18)';
      ctx.fillRect(0, 0, w, h);

      // Draw each waveform line
      lines.forEach((line) => {
        const [r, g, b] = line.color;
        const grad = ctx.createLinearGradient(0, 0, 0, h);
        grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0)`);
        grad.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, 0.45)`);
        grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

        ctx.beginPath();
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.25;
        ctx.shadowColor = `rgba(${r}, ${g}, ${b}, 0.55)`;
        ctx.shadowBlur = 10;

        const step = 4;
        for (let y = 0; y <= h + step; y += step) {
          const wavePos = y + scrollY * line.speed;
          const x =
            w * line.baseX +
            Math.sin(wavePos * line.freq + time * line.speed + line.phase) * line.amp;
          if (y === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.shadowBlur = 0;
      });

      // Animate the drifting particles
      particles.forEach((p) => {
        const line = lines[p.line];
        const [r, g, b] = line.color;
        const wavePos = p.y + scrollY * line.speed;
        const x =
          w * line.baseX +
          Math.sin(wavePos * line.freq + time * line.speed + line.phase) * line.amp;

        ctx.beginPath();
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.9)`;
        ctx.shadowColor = `rgba(${r}, ${g}, ${b}, 0.8)`;
        ctx.shadowBlur = 12;
        ctx.arc(x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        p.y += p.vy;
        if (p.y > h) {
          p.y = -10;
          p.vy = 0.4 + Math.random() * 0.8;
        }
      });

      // Animate the drifting music notes (subtle, no glow)
      notes.forEach((n) => {
        const line = lines[n.line];
        const [r, g, b] = line.color;
        const wavePos = n.y + scrollY * line.speed;
        const x =
          w * line.baseX +
          Math.sin(wavePos * line.freq + time * line.speed + line.phase) * line.amp;

        ctx.save();
        ctx.translate(x, n.y);
        ctx.rotate(n.rotation + Math.sin(time * 0.6 + n.y * 0.01) * 0.08);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.42)`;
        ctx.font = `${n.size}px 'Apple Symbols', 'Segoe UI Symbol', 'Noto Music', serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(n.glyph, 0, 0);
        ctx.restore();

        n.y += n.vy;
        if (n.y > h + 20) {
          n.y = -20;
          n.vy = 0.25 + Math.random() * 0.5;
          n.glyph = noteGlyphs[Math.floor(Math.random() * noteGlyphs.length)];
        }
      });

      time += 0.012;
      animationFrame = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [reduced]);

  if (reduced) return null;

  return <canvas ref={canvasRef} className="bg-flow" aria-hidden />;
}
